<template>
    <span class="list-record-link">
        <span class="list-record-link-value" :title="tooltip || label">
            <slot>{{ label }}</slot>
        </span>

        <!-- A real <router-link>, not a click handler: middle-click and
             right-click ▸ open in new tab are how people keep the list they are
             working through. `.stop` because the row click opens this list's own
             record — without it both fire and the wrong screen wins. -->
        <router-link :to="to" class="list-record-link-go" :title="goLabel" @click.stop>
            <b-icon icon="arrow-top-right" size="is-small" />
        </router-link>
    </span>
</template>

<script setup>
import { computed } from 'vue';

/**
 * A list cell naming another record, with a "go to" arrow revealed on hover.
 *
 * A relation cell is a dead end otherwise: the name of the client is on screen
 * and the only way to their fiche is to leave the list and search for them. The
 * arrow stays hidden until the pointer is in the cell so a grid of them still
 * reads as data rather than as a row of buttons — the same restraint as the
 * copy-to-clipboard affordance it mirrors.
 *
 * Use it for a **read-only** relation cell. A cell the organization made
 * inline-editable already owns its interaction (the pencil, the picker), and
 * stacking a second control on it is how a cell becomes unreadable.
 */
const props = defineProps({
    /** Where the arrow goes — a route location for the linked record's page. */
    to: { type: [String, Object], required: true },
    /** Displayed text. Ignored when the default slot is used. */
    label: { type: String, default: '' },
    /** Hover text on the value; defaults to the label, which is what a truncated cell needs. */
    tooltip: { type: String, default: null },
    /** Hover text on the arrow. Name the destination — "Open the contact" beats "Open". */
    goTitle: { type: String, default: null }
});

const goLabel = computed(() => props.goTitle || i18n.t('Open the record'));
</script>

<style scoped>
/* Fills the cell so hovering anywhere in it reveals the arrow, not just over
   the few characters of the name. */
.list-record-link {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
}
.list-record-link-value {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.list-record-link-go {
    opacity: 0;
    flex: 0 0 auto;
    line-height: 1;
    color: var(--bulma-text-weak);
    transition:
        opacity 0.15s ease,
        color 0.15s ease;
}
/* Keyboard users get it too: it is focusable while transparent, so revealing it
   on focus is what keeps it from being a control nobody can see themselves reach. */
.list-record-link:hover .list-record-link-go,
.list-record-link-go:focus-visible {
    opacity: 1;
}
.list-record-link-go:hover {
    color: var(--bulma-primary);
}
</style>
